import { useEffect, useReducer, useRef } from 'react';
import Button from 'components/ui-components/Button';
import NewAddress from './delivery_address_new_address';
import AddressBook from './delivery_address_address_book';
import DeliverToBranch from './delivery_address_branch_list';
import ChooseDeliveryAddress from './delivery_address_choose_address';
import ManualAddressForm from './manual_address_form';
import useErrorPush from '../../utils/useErrorPush';
import scrollElementIntoView from '../../utils/scrollIntoView';
import { validatePostcode } from './utils';
import { reducer, initialState } from './reducers/delivery_address_reducer';

const DeliveryAddress = ({
  initialName,
  account,
  guestTelephone,
  setCurrentStep,
  clearDeliveryService,
  addressEnquiryUrl,
  setDeliveryAddress,
  setGuestTelephone,
  setInvoiceAddressAsDeliveryAddress,
  setAddressMode,
  addressMode,
  signedIn,
  selectedDeliveryAddress,
  currentStep,
  invoiceAddress,
  deliveryAddressUrl,
  getAddressBook,
  setAddressFromAddressBook,
  configGenericError,
  toggleGenericError,
  processingState,
  step,
  branchAddress,
  branches,
  showBranchPanel,
  showNoBranches,
  getBranches,
  setBranchAddressFlag,
  addressBook,
}) => {
  const addressRef = useRef();
  const deliveryScrollRef = useRef();
  const errorRef = useRef();

  const [
    {
      addresses,
      name,
      loading,
      searchPostcode,
      searchPostcodeError,
      signedInPostcodeError,
      selectedAddress,
      telephone,
      selected,
      showNameError,
      showTelephoneError,
      guestAddressMode,
      showManualAddressForm,
      showManualAddressButton,
      postcodeErrorMessage,
      postcodeExcluded,
      isPostcodeValid,
      newAddressValid,
    },
    dispatch,
  ] = useReducer(reducer, {
    ...initialState,
    name: initialName,
    selectedAddress: {
      name: initialName,
      line_1: '',
      line_2: '',
      line_3: '',
      line_4: '',
      line_5: '',
      postcode: '',
    },
    telephone: account.telephone || guestTelephone || '',
  });

  useEffect(() => {
    if (showNameError || showTelephoneError) scrollElementIntoView(errorRef);

    useErrorPush(
      'Please enter a name, this will only be used to provide updates on this order',
      window.dataLayer && showTelephoneError,
    );

    useErrorPush(
      'Please enter a Telephone Number, this will only be used to provide updates on this order',
      window.dataLayer && showTelephoneError,
    );
  }, [showNameError, showTelephoneError]);

  const setGuestDeliveryAddress = address => {
    dispatch({ type: 'SET_GUEST_DELIVERY_ADDRESS', payload: address });

    scrollElementIntoView(deliveryScrollRef);
  };

  const handleTelephoneError = value => {
    dispatch({ type: 'SET_SHOW_TELEPHONE_ERROR', payload: value });
  };

  const hasValidContactDetails = () => {
    const hasName = name.length > 0;
    const hasTelephone = telephone.length > 0;

    dispatch({ type: 'SET_SHOW_NAME_ERROR', payload: !hasName });
    handleTelephoneError(!hasTelephone);

    return hasName && hasTelephone;
  };

  const confirmGuestDeliveryAddress = () => {
    if (!hasValidContactDetails()) return;

    setDeliveryAddress({
      ...selectedAddress,
      name,
    });
    setGuestTelephone(telephone);
  };

  const handleGuestContactNumber = e => {
    dispatch({ type: 'SET_TELEPHONE', payload: e.target.value });

    setGuestTelephone(e.target.value);
  };

  const handleGuestName = e => {
    dispatch({ type: 'SET_GUEST_NAME', payload: e.target.value });
  };

  const updateStep = () => {
    setCurrentStep('DELIVERY_DETAILS');
  };

  const editThisStep = () => {
    clearDeliveryService();
    setCurrentStep('DELIVERY_ADDRESS');
  };

  const setAddressModeRemoveAddresses = () => {
    dispatch({ type: 'CLEAR_POSTCODE_ADDRESSES' });
    setAddressMode('BRANCH_DELIVERY');
  };

  const getAddressesFromPostCode = () => {
    if (!hasValidContactDetails()) return;

    dispatch({ type: 'CLEAR_POSTCODE_ADDRESSES' });
    dispatch({ type: 'SET_LOADING', payload: true });

    $.ajax({
      url: addressEnquiryUrl,
      method: 'GET',
      dataType: 'json',
      data: {
        postcode: searchPostcode,
      },
      success: data => {
        dispatch({ type: 'SET_POSTCODE_ADDRESSES', payload: data });
        scrollElementIntoView(addressRef);
      },
      error: ({ responseText }) => {
        const errorMessage = JSON.parse(responseText).message;
        const excluded = JSON.parse(responseText).postcode_excluded;

        dispatch({
          type: 'SET_POSTCODE_LOOKUP_ERROR',
          payload: { errorMessage, excluded },
        });

        useErrorPush(
          'No Addresses found for postcode',
          window.dataLayer && searchPostcodeError === true,
        );

        scrollElementIntoView(addressRef);
      },
    });
  };

  const handleSearchPostCodeChange = e => {
    dispatch({
      type: 'SET_SEARCH_POSTCODE',
      payload: {
        postcode: e.target.value,
        isValid: validatePostcode(e.target.value).isValid,
      },
    });
  };

  const handleSignedInPostcodeError = value => {
    dispatch({ type: 'SET_SIGNED_IN_POSTCODE_ERROR', payload: value });
  };

  const globalHandleInputChange = e => {
    const newAddress = selectedAddress;
    const value = e.target.value;
    const inputName = e.target.name;

    newAddress[inputName] = value;

    dispatch({ type: 'SET_SELECTED_ADDRESS', payload: newAddress });
  };

  return (
    // Extract out into seperate component
    <div
      className={`checkout-panel delivery-address-step ${
        currentStep !== 'DELIVERY_ADDRESS' ? 'not-active' : ''
      }${processingState ? ' processing-payment' : ''}`}>
      <div className="checkout-header-title">
        <div className="checkout-header-title-left">
          <strong>{step}.</strong> Delivery Address
          {currentStep !== 'DELIVERY_ADDRESS' && <i className="icon-ok" />}
        </div>
        <div className="checkout-header-title-right">
          {currentStep !== 'DELIVERY_ADDRESS' && (
            <strong className="edit" onClick={editThisStep}>
              Edit
            </strong>
          )}
        </div>
      </div>
      {!signedIn && (
        <div className="checkout-sub-panel guest-address find-address">
          <h2 ref={errorRef}>Contact Details</h2>
          <div className="fl-on fl-fd-col">
            <div className="mw-50">
              <label htmlFor="guest-name">
                Name <strong className="required">*</strong>
              </label>
              <input
                type="text"
                id="guest-name"
                value={name}
                autoComplete="off"
                onChange={handleGuestName}
              />
              {showNameError && (
                <p data-testid="name-helper-text">
                  <span className="search-postcode--helper search-postcode--helper-error">
                    Please enter a name, this will only be used to provide
                    updates on this order
                  </span>
                </p>
              )}
            </div>
            <div className="mw-50 mt-10">
              <label htmlFor="guest-telephone">
                Contact Number <strong className="required">*</strong>
              </label>
              <input
                type="tel"
                autoComplete="off"
                id="guest-telephone"
                onChange={handleGuestContactNumber}
              />
              <p data-testid="tel-helper-text">
                <span
                  className={`search-postcode--helper ${
                    showTelephoneError ? 'search-postcode--helper-error' : ''
                  }`}>
                  Please be advised, this contact number will be shared with our
                  delivery partners to provide delivery updates, or they may
                  make contact should issues with your delivery arise
                </span>
              </p>
            </div>
          </div>

          <div className="checkout-sub-panel">
            <h2>Delivery Address</h2>
            <ChooseDeliveryAddress
              invoiceAddress={invoiceAddress}
              addressMode={addressMode}
              setAddressMode={setAddressMode}
              account={account}
              signed_in={signedIn}
              setAddressModeRemoveAddresses={setAddressModeRemoveAddresses}
              setInvoiceAddressAsDeliveryAddress={
                setInvoiceAddressAsDeliveryAddress
              }
            />
            <DeliverToBranch
              branchAddress={branchAddress}
              addresses={branches}
              showBranchPanel={showBranchPanel}
              showNoBranches={showNoBranches}
              addressMode={addressMode}
              getBranches={getBranches}
              setAddressFromAddressBook={setAddressFromAddressBook}
              selectedDeliveryAddress={selectedDeliveryAddress}
              setBranchAddressFlag={setBranchAddressFlag}
              hasValidContactDetails={hasValidContactDetails}
              signedIn={signedIn}
            />

            {!showBranchPanel && (
              <div className="input-address">
                <div className="manual-address-wrapper">
                  <div ref={addressRef} className="fl-fb-50">
                    <label htmlFor="guest-postcode-search">
                      Postcode <strong className="required">*</strong>
                    </label>
                    <div className="fl fw-w ai-c">
                      <input
                        type="text"
                        id="guest-postcode-search"
                        className="postcode guest-postcode-search"
                        value={searchPostcode}
                        onChange={handleSearchPostCodeChange}
                        autoComplete="off"
                      />
                      <button
                        className="fl-1 find-address-button"
                        onClick={getAddressesFromPostCode}
                        disabled={!isPostcodeValid}
                        data-testid={
                          !signedIn
                            ? 'guest-find-address'
                            : 'account-find-address'
                        }
                        type="button">
                        {loading ? (
                          <span className="loading">
                            <i className="icon-spinner icon-spin icon-large" />
                          </span>
                        ) : (
                          'Find Address'
                        )}
                      </button>
                      <div className="manual-select-wrapper">
                        {showManualAddressButton && !postcodeExcluded && (
                          <button
                            className="manual-select"
                            onClick={() =>
                              dispatch({
                                type: 'SET_SHOW_MANUAL_ADDRESS_FORM',
                                payload: true,
                              })
                            }
                            type="button">
                            Enter Address Manually
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {searchPostcodeError && (
                  <div>
                    <span className="search-postcode--error">
                      {postcodeErrorMessage}
                    </span>
                    {!postcodeExcluded && (
                      <NewAddress
                        addressMode={guestAddressMode}
                        addressEnquiryUrl={addressEnquiryUrl}
                        deliveryAddressUrl={deliveryAddressUrl}
                        getAddressBook={getAddressBook}
                        setAddressFromAddressBook={setAddressFromAddressBook}
                        setAddressMode={setAddressMode}
                        guestPostcodeError={searchPostcodeError}
                        signedIn={signedIn}
                        method="PUT"
                        configGenericError={configGenericError}
                        toggleGenericError={toggleGenericError}
                        initialShowManualAddressForm={searchPostcodeError}
                        handleTelephoneError={handleTelephoneError}
                        telephone={telephone}
                        guestName={name}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {addresses.length > 0 && (
            <div>
              <div className="available--addresses">
                <ul>
                  {addresses.map(address => {
                    const className =
                      selected === address ? 'active' : 'inactive';
                    return (
                      <li
                        key={address.short_name}
                        className={className}
                        onClick={() => {
                          setGuestDeliveryAddress(address);
                        }}>
                        {address.short_name}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          {showManualAddressForm && (
            <ManualAddressForm
              handleAddressLine1Change={globalHandleInputChange}
              handleAddressLine2Change={globalHandleInputChange}
              handleAddressLine3Change={globalHandleInputChange}
              handleAddressLine4Change={globalHandleInputChange}
              handlePostCodeChange={globalHandleInputChange}
              line1={selectedAddress.line_1}
              line2={selectedAddress.line_2}
              line3={selectedAddress.line_3}
              line4={selectedAddress.line_4}
              postcode={selectedAddress.postcode}
            />
          )}
          <div className=" fl-on fl-sm-on fl-jc-fs fl-sm-jc-fs ">
            {showManualAddressForm && (
              <button
                ref={deliveryScrollRef}
                disabled={!newAddressValid}
                onClick={confirmGuestDeliveryAddress}
                className="next-panel"
                type="button">
                Continue to Delivery Details
              </button>
            )}
          </div>
        </div>
      )}
      {signedIn && (
        <div>
          <ChooseDeliveryAddress
            invoiceAddress={invoiceAddress}
            addressMode={addressMode}
            setAddressMode={setAddressMode}
            account={account}
            signed_in={signedIn}
            setAddressModeRemoveAddresses={setAddressModeRemoveAddresses}
            setInvoiceAddressAsDeliveryAddress={
              setInvoiceAddressAsDeliveryAddress
            }
          />
          <DeliverToBranch
            branchAddress={branchAddress}
            addresses={branches}
            showBranchPanel={showBranchPanel}
            showNoBranches={showNoBranches}
            addressMode={addressMode}
            getBranches={getBranches}
            setAddressFromAddressBook={setAddressFromAddressBook}
            selectedDeliveryAddress={selectedDeliveryAddress}
            setBranchAddressFlag={setBranchAddressFlag}
            hasValidContactDetails={hasValidContactDetails}
            signedIn={signedIn}
          />
        </div>
      )}
      {/* Only render this on the delivery step to avoid performance issues
        with large quantities of addresses in an address book. This is a
        temporary fix TODO: implement pagination/lazy loading */}
      {signedIn &&
        addressMode === 'ADDRESS_BOOK' &&
        currentStep === 'DELIVERY_ADDRESS' && (
          <AddressBook
            type="delivery"
            addresses={addressBook}
            selectedAddress={selectedDeliveryAddress}
            setAddress={setAddressFromAddressBook}
          />
        )}
      {signedIn && (
        <NewAddress
          addressMode={addressMode}
          addressEnquiryUrl={addressEnquiryUrl}
          deliveryAddressUrl={deliveryAddressUrl}
          getAddressBook={getAddressBook}
          setAddressFromAddressBook={setAddressFromAddressBook}
          setAddressMode={setAddressMode}
          signedIn={signedIn}
          configGenericError={configGenericError}
          toggleGenericError={toggleGenericError}
          handleSignedInpostcodeError={handleSignedInPostcodeError}
          initialShowManualAddressForm={signedInPostcodeError}
          telephone={telephone}
          currentStep={currentStep}
        />
      )}
      {signedIn && addressMode === 'INVOICE_ADDRESS' && (
        <div className="checkout-sub-panel fl-on fl-sm-on next-step">
          <Button
            handleClick={() => {
              updateStep();
              if (addressMode === 'INVOICE_ADDRESS') {
                setInvoiceAddressAsDeliveryAddress();
              }
            }}
            className="set-address-button next-panel ff-body fw-600"
            testId="continue-to-delivery-options"
            disabled={!signedIn ? !addresses.length : !selectedDeliveryAddress}>
            Continue to Delivery Options
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeliveryAddress;
